import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SignalRService, SignalRMessage } from '../../core/signalr.service';
import { STANDALONE_COMPONENT_IMPORTS } from '../standalone-component-imports';

interface DisplayNotification {
  id: string;
  title: string;
  message: string;
  timestamp: Date;
  visible: boolean;
}

@Component({
    standalone: true,
    imports: STANDALONE_COMPONENT_IMPORTS,
    selector: 'pd-global-notifications',
  templateUrl: './global-notifications.component.html',
  styleUrls: ['./global-notifications.component.css']
})
export class GlobalNotificationsComponent implements OnInit, OnDestroy {
  
  public visibleNotifications: DisplayNotification[] = [];
  private allNotifications: any[] = []; 
  private subscription: Subscription = new Subscription();
  private notificationIdCounter = 0;

  constructor(private SignalRService: SignalRService) {}

  ngOnInit(): void {
    // Subscribe to new messages
    this.subscription.add(
      this.SignalRService.getMessages().subscribe(messages => {
        // Show only latest message as notification
        if (messages.length > 0) {
          const latestMessage = messages.at(-1);
          this.showNotification(latestMessage);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Show notification
   */
  private showNotification(message: SignalRMessage): void {
    
    const messageName = message.name || 'Notification';
    const notificationId = ++this.notificationIdCounter;
    
    const messageWithId = { ...message, notificationId };
    this.allNotifications.push(messageWithId);
    
    const notification: DisplayNotification = {
      id: `notif_${notificationId}`,
      title: messageName, 
      message: this.getSignalRMessage(message),
      timestamp: message.timestamp || new Date(),
      visible: true
    };


    // Add to visible list
    this.visibleNotifications.push(notification);
    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.closeNotification(notification.id);
    }, 5000);

    // Maximum 3 notifications simultaneously
    if (this.visibleNotifications.length > 3) {
      this.visibleNotifications.shift();
    }
  }

  /**
   * Close notification
   */
  public closeNotification(notificationId: string): void {
    this.visibleNotifications = this.visibleNotifications.filter(
      n => n.id !== notificationId
    );
    
    const numericId = parseInt(notificationId.replace('notif_', ''));
    this.allNotifications = this.allNotifications.filter(
      msg => msg.notificationId !== numericId
    );
  }

  /**
   * Get message text
   */
  private getSignalRMessage(message: SignalRMessage): string {
    return message.name || 'Notification';
  }

  /**
   * Get notification data (JSON payload) for display in body
   */
  public getNotificationData(notification: DisplayNotification): string {
    const notificationId = parseInt(notification.id.replace('notif_', ''));
    
    const signalRMessage = this.allNotifications.find(msg => 
      msg.notificationId === notificationId
    );
    
    if (signalRMessage && signalRMessage.data) {
      try {
        if (typeof signalRMessage.data === 'string') {
          return signalRMessage.data;
        }
        return JSON.stringify(signalRMessage.data);
      } catch (error) {
        console.warn('Failed to process notification data:', error);
        return signalRMessage.data?.toString() || '';
      }
    }
    
    return '';
  }
}
